import React from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Divider from '@material-ui/core/Divider';
import LockIcon from '@material-ui/icons/Lock';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import Container from 'common/Container';

/** Rutas de acreditación (mismo path que en `Routes.js`). */
const REGISTRATION_PATH_PRESS = '/pressstart';
const REGISTRATION_PATH_BUSINESS = '/business-level';

const MEDIA_KIT_PUBLIC_ORIGIN = String(
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SITE_URL) || ''
).replace(/\/$/, '');

/**
 * En `tnocon.tnonetwork.com` el bundle es el mismo pero esas rutas no se sirven en ese host;
 * los registros viven en el origen del media kit (p. ej. ads.tnonetwork.com).
 */
function registrationHref(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (typeof window !== 'undefined') {
    const host = String(window.location.hostname || '').toLowerCase();
    if (host === 'tnocon.tnonetwork.com') {
      const base = MEDIA_KIT_PUBLIC_ORIGIN || 'https://ads.tnonetwork.com';
      return `${base}${p}`;
    }
  }
  return p;
}

/** Alineado con landings hermanas: Guild (verde), Press Start (cian), Business Level (oro + índigo). */
const TRACK = {
  guild: {
    accent: '#228c5f',
    soft: 'rgba(34, 140, 95, 0.08)',
    chipBg: 'rgba(34, 140, 95, 0.2)',
    chipFg: '#0d3d28',
    btn: '#228c5f',
    btnHover: '#1b734c',
    btnText: '#ffffff',
  },
  press: {
    accent: '#00acc1',
    soft: 'rgba(0, 172, 193, 0.08)',
    chipBg: 'rgba(0, 188, 212, 0.22)',
    chipFg: '#004d56',
    btn: '#00acc1',
    btnHover: '#0097a7',
    btnText: '#ffffff',
  },
  business: {
    accent: '#c9a227',
    soft: 'rgba(201, 162, 39, 0.1)',
    chipBg: 'rgba(201, 162, 39, 0.28)',
    chipFg: '#3d3200',
    btn: '#c9a227',
    btnHover: '#b8941f',
    btnText: '#1a237e',
  },
};

/** Lockup TNO + CON (misma línea visual que Press Start / Business Level). */
const BrandTnoCon = ({ onDark = false, sxTno = {}, sxCon = {} }) => (
  <Box component="span" sx={{ whiteSpace: 'nowrap', display: 'inline' }}>
    <Box
      component="span"
      sx={{
        fontWeight: 900,
        letterSpacing: '-0.07em',
        textShadow:
          '0.02em 0 0 currentColor, -0.02em 0 0 currentColor, 0 0.02em 0 currentColor, 0 -0.02em 0 currentColor',
        ...sxTno,
      }}
    >
      TNO
    </Box>
    <Box
      component="span"
      sx={{
        fontWeight: 200,
        letterSpacing: '-0.07em',
        marginLeft: '-0.06em',
        color: onDark ? 'rgba(255,255,255,0.72)' : '#4a505c',
        ...sxCon,
      }}
    >
      CON
    </Box>
  </Box>
);

const EVENT_MAPS_URL =
  process.env.REACT_APP_TNOCON_MAPS_URL ||
  process.env.REACT_APP_PRESSSTART_MAPS_URL ||
  'https://share.google/W53TGyHp3m8uSLD5y';

const ecosystemAxes = [
  {
    title: 'Streaming',
    icon: LiveTvIcon,
    text: 'El impacto del vivo y la conexión humana.',
    accent: '#e53935',
    soft: 'rgba(229, 57, 53, 0.08)',
  },
  {
    title: 'On-Demand',
    icon: VideoLibraryIcon,
    text: 'El catálogo original disponible en todo momento.',
    accent: '#5e35b1',
    soft: 'rgba(94, 53, 177, 0.08)',
  },
  {
    title: 'Digest',
    icon: MenuBookIcon,
    text: 'La inteligencia editorial para quienes buscan profundidad.',
    accent: '#00695c',
    soft: 'rgba(0, 105, 92, 0.08)',
  },
];

const agendaDays = [
  {
    day: 'Día 1',
    label: 'Creators Guild',
    badge: 'Sesión interna',
    concept: 'Fortaleciendo el motor de TNO.',
    description:
      'Jornada exclusiva para el talento de la casa. En el TNO Media Lab, nuestros comunicadores se especializan en el nuevo ecosistema, el portafolio comercial y el roadmap tecnológico 2026. Porque para innovar hacia afuera, primero evolucionamos por dentro.',
    internal: true,
    track: 'guild',
  },
  {
    day: 'Día 2',
    label: 'Press Start',
    badge: 'Prensa e influencers',
    concept: 'El encendido oficial de la señal.',
    description:
      'Abrimos nuestras puertas a colegas y medios para presentar la evolución de nuestra señal multiplataforma. Una experiencia inmersiva para conocer las métricas reales y el impacto de nuestro ecosistema híbrido.',
    internal: false,
    registerTo: REGISTRATION_PATH_PRESS,
    registerLabel: 'Registro prensa',
    track: 'press',
  },
  {
    day: 'Día 3',
    label: 'The Business Level',
    badge: 'Marcas y aliados',
    concept: 'Alianzas estratégicas.',
    description:
      'El punto de encuentro para marcas y tomadores de decisiones. Rueda de negocios diseñada para consolidar proyectos, patrocinios y alianzas que definen el mercado digital del 2026.',
    internal: false,
    registerTo: REGISTRATION_PATH_BUSINESS,
    registerLabel: 'Registro marcas y aliados',
    track: 'business',
  },
];

const metrics = [
  { value: '30.000+', label: 'Visitas mensuales al streaming (señal en vivo y plataforma).', accent: '#00695c' },
  { value: '90.000+', label: 'Seguidores orgánicos en nuestro ecosistema social.', accent: '#00838f' },
  { value: '40+', label: 'Producciones originales On-Demand.', accent: '#5e35b1' },
  { value: '25+', label: 'Programas en vivo semanales.', accent: '#c62828' },
];

function AgendaDayCard({ block }) {
  const t = TRACK[block.track];
  return (
    <Grid item xs={12} md={4}>
      <Card
        variant="outlined"
        sx={{
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '4px solid',
          borderLeftColor: t.accent,
          bgcolor: t.soft,
        }}
      >
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {block.day}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800 }} gutterBottom>
          {block.label}
        </Typography>
        <Chip
          size="small"
          label={block.badge}
          sx={{
            alignSelf: 'flex-start',
            mb: 2,
            fontWeight: 600,
            bgcolor: t.chipBg,
            color: t.chipFg,
            border: block.internal ? `1px solid ${t.accent}` : 'none',
          }}
        />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          {block.concept}
        </Typography>
        <Typography color="textSecondary" sx={{ lineHeight: 1.8, flexGrow: 1 }}>
          {block.description}
        </Typography>
        {!block.internal && block.registerTo && (
          <Box mt={3}>
            <Button
              component="a"
              href={registrationHref(block.registerTo)}
              variant="contained"
              size="medium"
              fullWidth
              sx={{
                fontWeight: 700,
                bgcolor: t.btn,
                color: t.btnText,
                boxShadow: 'none',
                textDecoration: 'none',
                '&:hover': { bgcolor: t.btnHover, boxShadow: 'none' },
              }}
            >
              {block.registerLabel}
            </Button>
          </Box>
        )}
        {block.internal && (
          <Box mt={3} display="flex" alignItems="center" sx={{ color: t.chipFg }}>
            <LockIcon sx={{ fontSize: 18, mr: 1, color: t.accent }} />
            <Typography variant="caption">Solo equipo TNO · sin registro abierto</Typography>
          </Box>
        )}
      </Card>
    </Grid>
  );
}

const LandingTNOCon = () => (
  <Box>
    <Box
      minHeight={{ xs: 'auto', md: 'calc(100vh - 64px)' }}
      position="relative"
      sx={{
        background:
          'linear-gradient(130deg, rgba(6,11,33,0.97) 0%, rgba(21,32,83,0.96) 45%, rgba(0,187,255,0.55) 100%)',
        color: 'common.white',
        overflow: 'hidden',
        '&:before': {
          content: '" "',
          position: 'absolute',
          top: -100,
          right: -80,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,247,255,0.4), rgba(0,247,255,0))',
        },
      }}
    >
      <Container>
        <Box py={{ xs: 6, md: 10 }} position="relative" zIndex={2}>
          <Chip
            label={
              <span>
                <BrandTnoCon onDark />
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {' '}
                  2026 · The Hybrid Shift
                </Box>
              </span>
            }
            sx={{
              mb: 3,
              color: 'common.white',
              bgcolor: 'rgba(255,255,255,0.12)',
            }}
          />
          <Box
            component="figure"
            aria-label="TNO Network"
            sx={{
              m: 0,
              width: { xs: 280, sm: 340, md: 420 },
              height: { xs: 86, sm: 104, md: 128 },
              mb: 3,
              mt: 0,
              backgroundImage: 'url(/images/logos/logo_white.PNG)',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundPosition: 'left center',
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.15 }} gutterBottom>
            TNO CON 2026: The Hybrid Shift
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 920, opacity: 0.95, fontWeight: 400 }} paragraph>
            La primera conferencia de convergencia mediática donde la tecnología potencia el contenido. Un espacio
            exclusivo para marcas, prensa y creadores.
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 720, opacity: 0.92, fontWeight: 600 }} paragraph>
            Del 20 al 22 de abril · Las Mercedes, Caracas.
          </Typography>
          <Link
            href={EVENT_MAPS_URL}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{ color: 'common.white', fontWeight: 600 }}
          >
            Ver ubicación en mapas
          </Link>

          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            flexWrap="wrap"
            gap={2}
            mt={4}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button
              component="a"
              href={registrationHref(REGISTRATION_PATH_PRESS)}
              variant="contained"
              size="large"
              sx={{
                fontWeight: 700,
                px: 3,
                display: 'inline-block',
                textDecoration: 'none',
                bgcolor: TRACK.press.btn,
                color: TRACK.press.btnText,
                boxShadow: 'none',
                '&:hover': { bgcolor: TRACK.press.btnHover, boxShadow: 'none' },
              }}
            >
              Registro prensa
            </Button>
            <Button
              component="a"
              href={registrationHref(REGISTRATION_PATH_BUSINESS)}
              variant="contained"
              size="large"
              sx={{
                fontWeight: 700,
                px: 3,
                display: 'inline-block',
                textDecoration: 'none',
                bgcolor: TRACK.business.btn,
                color: TRACK.business.btnText,
                boxShadow: 'none',
                '&:hover': { bgcolor: TRACK.business.btnHover, boxShadow: 'none' },
              }}
            >
              Registro marcas y aliados
            </Button>
          </Box>
          <Typography variant="body2" sx={{ mt: 2, opacity: 0.85, maxWidth: 520 }}>
            <LockIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
            Acceso Creators Guild: talento TNO vía login interno (sin registro público).
          </Typography>
        </Box>
      </Container>
    </Box>

    <Container>
      <Box py={{ xs: 5, md: 8 }}>
        <Typography variant="h3" sx={{ fontWeight: 800 }} gutterBottom>
          El ecosistema TNO
        </Typography>
        <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 500, mb: 1 }}>
          ¿Quiénes somos?
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ lineHeight: 1.9, maxWidth: 900, mb: 4 }}>
          Somos un ecosistema de comunicación integral que no se detiene. Unimos la inmediatez con la profundidad a
          través de tres ejes:
        </Typography>
        <Grid container spacing={3}>
          {ecosystemAxes.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Card
                variant="outlined"
                sx={{
                  p: 3,
                  height: '100%',
                  borderLeft: '4px solid',
                  borderLeftColor: item.accent,
                  bgcolor: item.soft,
                }}
              >
                <Box
                  width={48}
                  height={48}
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                  sx={{ bgcolor: item.accent, color: 'common.white' }}
                >
                  <item.icon />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
                  {item.title}
                </Typography>
                <Typography color="textSecondary">{item.text}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>

    <Box sx={{ bgcolor: 'alternate.main' }}>
      <Container>
        <Box py={{ xs: 5, md: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 800 }} gutterBottom>
            La agenda: tres niveles de impacto
          </Typography>
          <Grid container spacing={3}>
            {agendaDays.map((block) => (
              <AgendaDayCard key={block.label} block={block} />
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>

    <Container>
      <Box py={{ xs: 5, md: 8 }}>
        <Typography variant="h3" sx={{ fontWeight: 800 }} gutterBottom>
          El valor de lo orgánico
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ lineHeight: 1.9, maxWidth: 800, mb: 4 }}>
          No perseguimos números vacíos; gestionamos audiencias fieles.
        </Typography>
        <Grid container spacing={2}>
          {metrics.map((m) => (
            <Grid item xs={12} sm={6} key={m.value}>
              <Card
                variant="outlined"
                sx={{
                  p: 2.5,
                  height: '100%',
                  borderLeft: '4px solid',
                  borderLeftColor: m.accent,
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 900, color: m.accent }} gutterBottom>
                  {m.value}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {m.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, maxWidth: 720 }}>
          Conectamos con personas reales en sus manos y en sus hogares.
        </Typography>
      </Box>
    </Container>

    <Box
      sx={{
        background:
          'linear-gradient(120deg, rgba(53,90,140,0.12) 0%, rgba(0,187,255,0.08) 100%)',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container>
        <Box py={{ xs: 5, md: 7 }} display="flex" flexWrap="wrap" alignItems="center" gap={3}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EventAvailableIcon sx={{ fontSize: 36 }} />
          </Box>
          <Box flex={1} minWidth={{ xs: '100%', sm: 280 }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }} gutterBottom>
              Acreditación inteligente
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ lineHeight: 1.85, maxWidth: 800 }}>
              Tras tu registro, recibirás tu <strong>Smart Ticket</strong> digital: tu llave única de entrada para vivir
              la experiencia TNO CON, gestionar tu acceso y recibir el material exclusivo de la conferencia.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>

    <Box component="footer" sx={{ bgcolor: 'grey.900', color: 'grey.100' }}>
      <Container>
        <Box py={{ xs: 5, md: 6 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
            <BrandTnoCon onDark sxTno={{ color: 'common.white' }} sxCon={{ color: 'rgba(255,255,255,0.75)' }} /> 2026
          </Typography>
          <Typography variant="body1" paragraph sx={{ opacity: 0.9 }}>
            Edif. Manzanares, Piso 2, Las Mercedes, Caracas.
          </Typography>
          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.15)' }} />
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            <Link href="https://tno.network" target="_blank" rel="noreferrer" color="inherit" underline="hover">
              tno.network
            </Link>
            {' · '}
            <Link
              href="https://www.instagram.com/tnonetwork/"
              target="_blank"
              rel="noreferrer"
              color="inherit"
              underline="hover"
            >
              @tnonetwork
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', opacity: 0.75 }}>
            El futuro no se sintoniza, se construye online.
          </Typography>
          <Box mt={3}>
            <Button href={EVENT_MAPS_URL} target="_blank" rel="noreferrer" variant="outlined" size="small" sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,0.5)' }}>
              Cómo llegar
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  </Box>
);

export default LandingTNOCon;
